/**
 * Which of the three surfaces this bundle should render.
 *
 * Split out and made pure because the one case that matters most cannot be run
 * here: inside the iPad app, Capacitor serves the bundle from
 * `capacitor://localhost/` and the pathname is `/`. Route on the path alone
 * and the day `/` becomes the marketing site, every child's tablet opens a
 * marketing page instead of Howcomet — silently, and only on the device nobody
 * tests on. So the native check comes first and never redirects.
 *
 * The address plan:
 *
 *   /          marketing (nginx serves it; this bundle is not involved)
 *   /app       the child's app
 *   /parents   the grown-ups' page
 *   /api       the service
 */
export type Surface = "kid" | "parent" | "redirect-to-app";

export const APP_PATH = "/app";
export const PARENT_PATH = "/parents";

export function surfaceFor(location: { protocol: string; pathname: string }): Surface {
  /*
    Anything that is not http(s) is a native shell — `capacitor:` today,
    `ionic:` or `file:` if the wrapper ever changes. Checked by exclusion
    rather than by naming capacitor, because the failure mode of guessing
    wrong is a blank app on a child's iPad and the failure mode of being
    broad here is nothing at all: a native shell has no marketing site to
    serve.
  */
  const isWeb = location.protocol === "http:" || location.protocol === "https:";
  if (!isWeb) return "kid";

  if (pathIs(location.pathname, PARENT_PATH)) return "parent";
  if (pathIs(location.pathname, APP_PATH)) return "kid";

  /*
    In production nginx never routes anything else to this bundle, so this is
    reached only by the dev server, which serves index.html for every path.
    Sending it to /app keeps `npm run dev` working at localhost:5173 without
    the bundle having to pretend to be the marketing site.
  */
  return "redirect-to-app";
}

/** True for the path itself and anything under it, but not for a prefix match. */
function pathIs(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}
